// Composited preview for the item create form.
//
// An item image is a full 125x225 avatar canvas, not a small sprite with a
// position, so "does it line up" is unanswerable from the file on its own: the
// artwork is a few pixels in a mostly transparent frame. This draws the
// default islander underneath and the pending upload in its own layer slot, so
// the answer is visible before submitting. Two of the seven rejection reasons
// are alignment and legibility.
//
// Display only. The layer images come from the asset host in production, which
// taints the canvas, and nothing here reads pixels back out.
function wireAvatarPreview(root) {
  const canvas = root.querySelector('canvas')
  if (!canvas) return

  const ctx = canvas.getContext('2d')
  const layers = JSON.parse(root.dataset.previewLayers || '[]')
  const categories = JSON.parse(root.dataset.previewCategories || '{}')
  const input = document.querySelector('input[data-image-preview]')
  const categorySelect = document.getElementById('item_category')
  if (!input) return

  const rank = (name) => (name in categories ? categories[name] : 999)
  let pending = null // { image, category }

  const load = (src) =>
    new Promise((resolve) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = () => resolve(null)
      img.src = src
    })

  // Defaults are fetched once; the pending upload is redrawn on every change.
  const defaults = Promise.all(
    layers.map((layer) => load(layer.url).then((image) => (image ? { ...layer, image } : null)))
  ).then((loaded) => loaded.filter(Boolean))

  const redraw = async () => {
    const base = await defaults
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // The pending item replaces the default in its own category, so a new
    // shirt covers the default shirt rather than layering on top of it.
    const stack = base
      .filter((layer) => !pending || layer.category !== pending.category)
      .concat(pending ? [pending] : [])
      .sort((a, b) => rank(a.category) - rank(b.category))

    stack.forEach((layer) => ctx.drawImage(layer.image, 0, 0, canvas.width, canvas.height))
  }

  input.addEventListener('change', async function () {
    const file = this.files[0]
    if (!file) {
      pending = null
      return redraw()
    }
    const image = await load(URL.createObjectURL(file))
    pending = image ? { image, category: categorySelect ? categorySelect.value : null } : null
    redraw()
  })

  if (categorySelect) {
    categorySelect.addEventListener('change', () => {
      if (pending) pending.category = categorySelect.value
      redraw()
    })
  }

  redraw()
}

// The item page's try-on: the viewer's own equipped look with the viewed
// item already swapped into its slot, pre-ordered by the server. No inputs
// to watch — just composite the list once.
function wireTryOn(root) {
  const canvas = root.querySelector('canvas')
  if (!canvas) return

  const ctx = canvas.getContext('2d')
  const layers = JSON.parse(root.dataset.tryOnLayers || '[]')

  const load = (src) =>
    new Promise((resolve) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = () => resolve(null)
      img.src = src
    })

  Promise.all(layers.map((layer) => load(layer.url))).then((images) => {
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    images.filter(Boolean).forEach((image) => ctx.drawImage(image, 0, 0, canvas.width, canvas.height))
  })
}

// Moderation queue: hovering a pending item's art previews it composited
// over the default islander; moving away reverts. Clicking pins the preview
// (and is the whole interaction on touch and keyboard). The layer list is
// pre-ordered by the server; composition happens once, lazily.
function wireModTry(button) {
  if (button.dataset.wired) return
  button.dataset.wired = '1'

  const img = button.querySelector('img')
  const canvas = button.querySelector('canvas')
  if (!canvas) return

  let pinned = false
  let hovering = false

  const drawOnce = () => {
    if (canvas.dataset.drawn) return
    canvas.dataset.drawn = '1'
    const ctx = canvas.getContext('2d')
    const layers = JSON.parse(button.dataset.modTryLayers || '[]')
    Promise.all(layers.map((layer) => new Promise((resolve) => {
      const image = new Image()
      image.onload = () => resolve(image)
      image.onerror = () => resolve(null)
      image.src = layer.url
    }))).then((images) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      images.filter(Boolean).forEach((image) => ctx.drawImage(image, 0, 0, canvas.width, canvas.height))
    })
  }

  const render = () => {
    const show = pinned || hovering
    if (show) drawOnce()
    canvas.hidden = !show
    img.hidden = show
    button.classList.toggle('mod-try-on', show)
    button.classList.toggle('mod-try-pinned', pinned)
  }

  button.addEventListener('click', () => { pinned = !pinned; render() })

  // Hover preview only where hover is a real input mode — on touch screens
  // mouseenter fires alongside taps and would make the pin toggle feel broken.
  if (window.matchMedia('(hover: hover)').matches) {
    button.addEventListener('mouseenter', () => { hovering = true; render() })
    button.addEventListener('mouseleave', () => { hovering = false; render() })
  }
}

document.addEventListener('turbo:load', () => {
  document.querySelectorAll('.avatar-preview').forEach(wireAvatarPreview)
  document.querySelectorAll('[data-try-on-layers]').forEach(wireTryOn)
  document.querySelectorAll('.mod-try').forEach(wireModTry)
})
